import React, { createContext, useState, useContext, useEffect } from "react";
import apiService from "../services/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem("access_token");
    const userData = localStorage.getItem("user");

    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error("Failed to parse user data:", error);
        logout();
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await apiService.login(username, password);

      if (response.success && response.access_token) {
        const userData = {
          username: response.user?.username || username,
          permission: response.user?.permission || "viewer",
          id: response.user?.id,
          assigned_customers: response.user?.assigned_customers || null,
          partnership_id: response.user?.partnership_id || null,
          partnership: response.user?.partnership || null,
        };

        localStorage.setItem("access_token", response.access_token);
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);

        // Set selected company/partnership based on user's partnership
        if (response.user?.partnership) {
          const partnershipData = {
            id: response.user.partnership.id,
            name: response.user.partnership.name,
            displayName: response.user.partnership.name,
            logo: `/images/${response.user.partnership.name
              .toLowerCase()
              .replace(/\s+/g, "")}.png`,
            backgroundColor: "#0f172a",
          };
          localStorage.setItem(
            "selected_company",
            JSON.stringify(partnershipData)
          );
        }

        return { success: true };
      } else {
        return { success: false, error: response.error || "Login failed" };
      }
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        error: error.response?.data?.error || error.message || "Login failed",
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const hasPermission = (requiredPermission) => {
    if (!user) return false;

    const permissions = {
      viewer: 1,
      salary_viewer: 1.5,
      site_admin: 2,
      admin: 3,
      owner: 4,
    };

    const userLevel = permissions[user.permission] || 0;
    const requiredLevel = permissions[requiredPermission] || 0;

    return userLevel >= requiredLevel;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasPermission,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
